import React, { useCallback, useEffect, useRef, useState } from 'react';
import UsersList from './UsersList';
import ExternalUser from './ExternalUser';
import { getUsersList, sendInvite } from '../../api/user';
import Userprofile from '../Userprofile/Userprofile';

export interface IUserList {
  id: number;
  first_name: string;
  last_name: string;
  number: string;
  email: string;
  login_status: string;
  is_tenk: boolean;
  is_gateway_member: boolean;
  time_zone: string;
  profile_url: string | null;
  badge: string;
  country: string;
}

interface IPagination {
  page: number;
  next: number | null;
  count?: number;
}

const InvitePeople: React.FC<{
  roomId?: string;
  open: boolean;
  setOpen: () => void;
}> = ({ roomId, open, setOpen }) => {
  const [allUsers, setAllUsers] = useState<IUserList[]>([]);
  const [fans, setFans] = useState<IUserList[]>([]);
  const [friends, setFriends] = useState<IUserList[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedUsers, setSelectedUsers] = useState<IUserList[]>([]);
  const [emails, setEmails] = useState<string[]>([]);
  const [texts, setTexts] = useState<string[]>([]);
  const [isUserListLoading, setIsUserListLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [openNotification, setOpenNotification] = useState<boolean>(false);
  const isSearchingRef = useRef(false);
  const [copyLinkTooltip, setCopyLinkTooltip] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<{ [key: string]: string }>({
    all: '',
    fans: '',
    friends: '',
  });
  const [isEnabled, setIsEnabled] = useState<{ email: boolean; text: boolean }>(
    {
      email: false,
      text: false,
    }
  );
  const [errors, setErrors] = useState<{
    emailError: string;
    phoneError: string;
  }>({
    emailError: '',
    phoneError: '',
  });

  const pageRef = useRef<{ currentPage: number; nextPage: number | null }>({
    currentPage: 1,
    nextPage: 1,
  });
  const loaderRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const abortRefs = useRef<[AbortController, AbortController, AbortController]>(
    [new AbortController(), new AbortController(), new AbortController()]
  );

  const handleSearchChange = useCallback((tab: string, query: string) => {
    setSearchQuery((prev) => ({ ...prev, [tab]: query }));
    setIsUserListLoading(true);

    if (tab === 'all') setAllUsers([]);
    if (tab === 'fans') setFans([]);
    if (tab === 'friends') setFriends([]);

    pageRef.current = { currentPage: 1, nextPage: 1 };
  }, []);

  const handleClickOnSelectUser = useCallback(
    (item: IUserList) => {
      const isExists = selectedUsers.some((i) => i.id === item.id);
      if (isExists) {
        setSelectedUsers(selectedUsers.filter((i) => i.id !== item.id));
      } else {
        if (selectedUsers.length >= 50) {
          setError(true);
          return;
        }
        setSelectedUsers([...selectedUsers, item]);
      }
    },
    [selectedUsers]
  );

  const getCurrentSearchQuery = useCallback(
    (tab: number) => {
      if (tab === 0) return searchQuery.all;
      if (tab === 1) return searchQuery.fans;
      if (tab === 2) return searchQuery.friends;
      return '';
    },
    [searchQuery]
  );

  const renderSearchInput = (tab: 'all' | 'fans' | 'friends') => (
    <div className="relative">
      <button
        type="button"
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-2xl leading-none hover:opacity-70"
        aria-label="Clear search"
        onClick={() => handleSearchChange(tab, '')}
      >
        <i className="ri-search-line"></i>
      </button>
      <input
        className="input-border !px-12"
        type="search"
        placeholder="Invite others by name or Email"
        value={searchQuery[tab]}
        onChange={(e) => handleSearchChange(tab, e.target.value)}
      />
      {searchQuery[tab] && (
        <button
          type="button"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gradient-pink text-2xl leading-none hover:opacity-70"
          aria-label="Clear search"
          onClick={() => handleSearchChange(tab, '')}
        >
          <i className="ri-close-line"></i>
        </button>
      )}
    </div>
  );

  // --- API call ---
  const getAllUsers = useCallback(
    async (query: string, page: number) => {
      if (selectedTab === 3) return;

      // Abort previous requests for this tab
      abortRefs.current.forEach((ref) => ref.abort());
      abortRefs.current[selectedTab] = new AbortController();

      setIsUserListLoading(true);
      try {
        const res: {
          data: {
            users: IUserList[];
            pagination: IPagination;
          };
        } | null = await getUsersList(
          page,
          query,
          selectedTab === 0 ? 'all' : selectedTab === 1 ? 'fans' : 'friends',
          abortRefs.current[selectedTab].signal
        );

        if (!res) return;

        const userType = JSON.parse((res as any).config.data)?.user_type;

        const updater = (prev: IUserList[]) => {
          if (page === 1) return res.data.users;
          // avoid duplicates
          const existingIds = new Set(prev.map((i) => i.id));
          const newUsers = res.data.users.filter((u) => !existingIds.has(u.id));
          return [...prev, ...newUsers];
        };

        if (!userType) setAllUsers(updater);
        else if (userType === 'fans') setFans(updater);
        else if (userType === 'friends') setFriends(updater);

        // Update pagination
        pageRef.current.currentPage = res.data.pagination.page;
        pageRef.current.nextPage = res.data.pagination.next;
      } catch (err) {
        if ((err as any).name !== 'AbortError') console.log(err);
      } finally {
        setIsUserListLoading(false);
      }
    },
    [selectedTab]
  );

  const delayedSearch = useCallback(
    (query: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      isSearchingRef.current = true;
      setIsUserListLoading(true);

      pageRef.current = { currentPage: 1, nextPage: 1 };

      timerRef.current = setTimeout(() => {
        getAllUsers(query, 1).finally(() => {
          isSearchingRef.current = false;
        });
      }, 500);
    },
    [getAllUsers]
  );

  useEffect(() => {
    delayedSearch(getCurrentSearchQuery(selectedTab));
  }, [selectedTab, delayedSearch, getCurrentSearchQuery]);

  useEffect(() => {
    if (selectedTab === 3 || !loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            !isUserListLoading &&
            !isSearchingRef.current &&
            pageRef.current.nextPage
          ) {
            getAllUsers(
              getCurrentSearchQuery(selectedTab),
              pageRef.current.nextPage
            );
          }
        });
      },
      { root: null, rootMargin: '0px' }
    );
    const ref = loaderRef;

    observer.observe(ref.current!);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
      observer.disconnect();
    };
  }, [getAllUsers, isUserListLoading, selectedTab, getCurrentSearchQuery]);

  // --- Error auto-clear ---
  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => setError(false), 3000);
    return () => clearTimeout(timeout);
  }, [error]);

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-end bg-black/50 backdrop-blur-sm">
          <div className="h-dvh max-w-[700px] w-full bg-[#1D0232] p-5">
            <div className="grid grid-cols-2 items-center relative">
              <div>
                <button
                  className="text-2xl text-gradient-pink font-bold"
                  onClick={setOpen}
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
              </div>
              <h3 className="text-gradient-blue text-center uppercase text-[20px] font-semibold absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4">
                Invite People
              </h3>
              <div className="text-end">
                <button
                  className={`text-gradient-pink font-semibold ${
                    selectedUsers.length || isEnabled.email || isEnabled.text
                      ? ''
                      : 'pointer-events-none opacity-70 text-[16px]'
                  }`}
                  onClick={async () => {
                    const enabled: string[] = [];
                    if (isEnabled.email) {
                      if (emails.length === 0) {
                        setErrors({
                          ...errors,
                          emailError: 'Add email to send invite',
                        });
                        setSelectedTab(3);
                        return;
                      }
                      enabled.push('email');
                    }
                    if (isEnabled.text) {
                      if (texts.length === 0) {
                        setErrors({
                          ...errors,
                          phoneError: 'Add phone to send invite',
                        });
                        setSelectedTab(3);
                        return;
                      }
                      enabled.push('text');
                    }
                    const res = await sendInvite(
                      roomId!,
                      ['Individuals'],
                      selectedUsers.map((i) => i.id.toString()),
                      enabled,
                      isEnabled.email ? emails : [],
                      isEnabled.text ? texts : []
                    );
                    if (res) {
                      setSuccessMessage(res.data.message);
                      setOpenNotification(true);
                      setEmails([]);
                      setTexts([]);
                      setIsEnabled({ email: false, text: false });
                      setTimeout(() => setOpen(), 1500);
                    }
                  }}
                >
                  <span className="hidden md:block">Send Invite</span>
                  <span className="block md:hidden">Done</span>
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <h6 className="text-[16px] font-normal uppercase text-[#AE8A27]">
                You can select upto 50 people{' '}
                {selectedUsers.length > 0 && `(${selectedUsers.length})`}
              </h6>
              <div className="relative">
                <button
                  className="text-gradient-orange flex items-center gap-1"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    setCopyLinkTooltip(true);
                    setTimeout(() => setCopyLinkTooltip(false), 2000);
                  }}
                >
                  <span className="max-md:hidden">Copy Link</span>{' '}
                  <i className="ri-file-copy-fill text-2xl"></i>
                </button>
                {copyLinkTooltip && (
                  <div className="absolute top-full px-2 py-1 text-sm bg-[#830892] text-white rounded z-[999]">
                    Copied
                  </div>
                )}
              </div>
            </div>

            <div
              className={`flex flex-nowrap overflow-auto max-w-[460px] items-center gap-2 ${
                selectedUsers.length > 0 ? 'pb-3 pt-5' : ''
              }`}
            >
              {selectedUsers.map((item) => (
                <div
                  key={item.id}
                  className="w-full max-w-20 flex flex-col items-center justify-center flex-shrink-0"
                >
                  <div className="relative">
                    <Userprofile item={item} />
                    <button
                      className="absolute -top-5 -right-5 bg-none p-0 border-none text-gradient-pink text-2xl font-semibold"
                      onClick={() =>
                        setSelectedUsers(
                          selectedUsers.filter((i) => i.id !== item.id)
                        )
                      }
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                  <h4 className="line-clamp-1 text-base font-normal text-center text-primary mt-1">
                    {item.first_name} {item.last_name}
                  </h4>
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-start justify-start mb-3">
                {['All', 'Fans', 'Friends', 'External'].map((tab, index) => (
                  <button
                    key={tab}
                    className={`px-4 py-2 text-[16px] ${
                      selectedTab === index
                        ? 'text-gradient-orange'
                        : 'text-[#828282]'
                    }`}
                    onClick={() => {
                      if (selectedTab === index) return;
                      setSearchQuery({ all: '', fans: '', friends: '' });
                      pageRef.current.currentPage = 1;

                      // IMPORTANT: reset state BEFORE fetch
                      setIsUserListLoading(true);

                      if (index === 0) setAllUsers([]);
                      if (index === 1) setFans([]);
                      if (index === 2) setFriends([]);

                      setSelectedTab(index);
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {selectedTab === 0 && (
                <>
                  {renderSearchInput('all')}
                  <UsersList
                    type="all"
                    userData={allUsers}
                    handleClickOnSelectUser={handleClickOnSelectUser}
                    selectedUsers={selectedUsers}
                    isUserListLoading={isUserListLoading}
                    ref={loaderRef}
                  />
                </>
              )}
              {selectedTab === 1 && (
                <>
                  {renderSearchInput('fans')}
                  <UsersList
                    type="fans"
                    userData={fans}
                    handleClickOnSelectUser={handleClickOnSelectUser}
                    selectedUsers={selectedUsers}
                    isUserListLoading={isUserListLoading}
                    ref={loaderRef}
                  />
                </>
              )}
              {selectedTab === 2 && (
                <>
                  {renderSearchInput('friends')}
                  <UsersList
                    type="friends"
                    userData={friends}
                    handleClickOnSelectUser={handleClickOnSelectUser}
                    selectedUsers={selectedUsers}
                    isUserListLoading={isUserListLoading}
                    ref={loaderRef}
                  />
                </>
              )}
              {selectedTab === 3 && (
                <ExternalUser
                  isEnabled={isEnabled}
                  setIsEnabled={setIsEnabled}
                  emails={emails}
                  setEmails={setEmails}
                  texts={texts}
                  setTexts={setTexts}
                  errors={errors}
                  setErrors={setErrors}
                />
              )}
            </div>

            {openNotification && (
              <div className="fixed left-1/2 -translate-x-1/2 bottom-7 overflow-hidden p-[1px] after:content-[''] after:absolute after:inset-0 after:rounded-xl after:p-[5px] after:pointer-events-none after:z-0 gradient-pink-border">
                <div className="relative z-[1] bg-[#3d026c] flex gap-2 p-3 rounded-xl items-center">
                  <h6 className="text-primary m-0 text-[16px] whitespace-nowrap">
                    {successMessage}
                  </h6>
                  <button
                    className="text-primary text-2xl hover:opacity-55"
                    onClick={() => setOpenNotification(false)}
                  >
                    <i className="ri-close-circle-fill"></i>
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="fixed left-1/2 -translate-x-1/2 bottom-7 overflow-hidden p-[1px] after:content-[''] after:absolute after:inset-0 after:rounded-xl after:p-[5px] after:pointer-events-none after:z-0 gradient-pink-border">
                <div className="relative z-[1] bg-[#3d026c] flex gap-2 p-3 rounded-xl items-center">
                  <h6 className="text-red-500 m-0 text-[16px] whitespace-nowrap">
                    You can select upto 50 people
                  </h6>
                  <button
                    className="text-red-500 text-2xl hover:opacity-55"
                    onClick={() => setError(false)}
                  >
                    <i className="ri-close-circle-fill"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default InvitePeople;
